/**
 * Graph Editor Page
 *
 * Create or edit knowledge graph metadata
 */
import React, { useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeftIcon, XMarkIcon } from '@heroicons/react/24/outline'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import { knowledgeGraphStore } from '@/stores/KnowledgeGraphStore'
import { workspaceStore } from '@/stores/WorkspaceStore'
import { GraphType, GraphStatus, KnowledgeGraphCreate, KnowledgeGraphUpdate } from '@/types'
import toast from 'react-hot-toast'

const GraphEditorPage: React.FC = observer(() => {
  const navigate = useNavigate()
  const { graphId } = useParams<{ graphId: string }>()
  const isEditing = !!graphId

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [graphType, setGraphType] = useState<GraphType>(GraphType.SEMANTIC)
  const [status, setStatus] = useState<GraphStatus>(GraphStatus.DRAFT)
  const [tags, setTags] = useState<string[]>([])
  const [tagInput, setTagInput] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (isEditing && graphId) {
      knowledgeGraphStore.fetchGraphById(graphId).then((graph) => {
        if (graph) {
          setName(graph.name)
          setDescription(graph.description || '')
          setGraphType(graph.graph_type)
          setStatus(graph.status)
          setTags(graph.tags || [])
        }
      })
    }
  }, [isEditing, graphId])

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()])
      setTagInput('')
    }
  }

  const handleRemoveTag = (tag: string) => {
    setTags(tags.filter((t) => t !== tag))
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddTag()
    }
  }

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Graph name is required')
      return
    }

    const currentWorkspaceId = workspaceStore.currentWorkspace?.id
    if (!currentWorkspaceId && !isEditing) {
      toast.error('No workspace selected')
      return
    }

    setIsSaving(true)

    try {
      if (isEditing && graphId) {
        const updateData: KnowledgeGraphUpdate = {
          name: name.trim(),
          description: description.trim() || undefined,
          status,
          tags,
        }

        await knowledgeGraphStore.updateGraph(graphId, updateData)
        toast.success('Graph updated successfully')
        navigate(`/knowledge-graphs/${graphId}`)
      } else {
        const createData: KnowledgeGraphCreate = {
          name: name.trim(),
          description: description.trim() || undefined,
          graph_type: graphType,
          status,
          workspace_id: currentWorkspaceId!,
          tags,
        }

        const newGraph = await knowledgeGraphStore.createGraph(createData)
        toast.success('Graph created successfully')
        navigate(`/knowledge-graphs/${newGraph.id}`)
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to save graph')
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    if (isEditing && graphId) {
      navigate(`/knowledge-graphs/${graphId}`)
    } else {
      navigate('/knowledge-graphs')
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <button
            onClick={handleCancel}
            className="flex items-center text-muted-foreground hover:text-foreground mb-2"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back
          </button>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">
            {isEditing ? 'Edit Knowledge Graph' : 'Create Knowledge Graph'}
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            {isEditing
              ? 'Update the metadata for your knowledge graph'
              : 'Create a new knowledge graph to organize and connect information'}
          </p>
        </div>
      </div>

      {/* Form */}
      <Card>
        <div className="space-y-6">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Product Knowledge Base"
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the purpose of this knowledge graph..."
              rows={4}
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base"
            />
          </div>

          {/* Graph Type (only for new graphs) */}
          {!isEditing && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Graph Type <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {Object.values(GraphType).map((type) => (
                  <label
                    key={type}
                    className={`relative flex cursor-pointer rounded-lg border p-4 focus:outline-none ${
                      graphType === type
                        ? 'border-primary-600 ring-2 ring-primary-600 bg-primary-50'
                        : 'border-border hover:border-border'
                    }`}
                  >
                    <input
                      type="radio"
                      name="graph-type"
                      value={type}
                      checked={graphType === type}
                      onChange={(e) => setGraphType(e.target.value as GraphType)}
                      className="sr-only"
                    />
                    <div className="flex flex-1">
                      <div className="flex flex-col">
                        <span className="block text-sm font-medium text-foreground">
                          {type}
                        </span>
                        <span className="mt-1 flex items-center text-xs text-muted-foreground">
                          {getGraphTypeDescription(type)}
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as GraphStatus)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base"
            >
              {Object.values(GraphStatus).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Tags
            </label>
            <div className="space-y-2">
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-500/15 text-primary-800"
                    >
                      {tag}
                      <button
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-2 text-primary-600 hover:text-primary-800"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Add a tag..."
                  className="flex-1 px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base"
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleAddTag}
                  disabled={!tagInput.trim()}
                >
                  Add
                </Button>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t border-border">
            <Button variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={isSaving || !name.trim()}
              isLoading={isSaving}
            >
              {isEditing ? 'Update Graph' : 'Create Graph'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
})

function getGraphTypeDescription(type: GraphType): string {
  switch (type) {
    case GraphType.ONTOLOGY:
      return 'Formal representation of concepts and relationships'
    case GraphType.SEMANTIC:
      return 'Meaning and context-based knowledge'
    case GraphType.CAUSAL:
      return 'Cause and effect relationships'
    case GraphType.TEMPORAL:
      return 'Time-based events and sequences'
    case GraphType.HIERARCHICAL:
      return 'Parent-child tree structures'
    case GraphType.NETWORK:
      return 'Complex interconnected nodes'
    case GraphType.WORKFLOW:
      return 'Process flows and task dependencies'
    case GraphType.AGENT_MEMORY:
      return 'Agent learning and memory storage'
    default:
      return ''
  }
}

GraphEditorPage.displayName = 'GraphEditorPage'

export default GraphEditorPage
